import { useState, useRef, useEffect, type ReactElement } from 'react';
import { createPortal } from 'react-dom';
import EmojiPicker, { type EmojiClickData } from 'emoji-picker-react';
import {
  Check, CheckCheck, MoreVertical, Reply, Forward, Pencil, Trash2, Download,
  Play, Pause, Pin, PhoneMissed, User, FileText, CheckSquare, Smile, FileAudio, Send, Copy,
} from 'lucide-react';
import { formatHoraSP, parseSPDate, pixDoFluxoNativo, formatCpfCnpj, type ChavePixFluxo } from '@fiodecobre/shared';
import { Avatar } from '../../../components/Avatar';
import { atendimentoApi } from '../lib/api';
import { ThumbnailPdf } from './ThumbnailPdf';
import { LightboxImagem } from './LightboxImagem';
import { LinkPreviewCard } from './LinkPreview';
import type { Contato, Inbox, Mensagem } from '../lib/tipos';

const URL_REGEX = /(https?:\/\/[^\s<]+)/g;

function primeiraUrl(texto: string | null | undefined): string | null {
  if (!texto) return null;
  const m = texto.match(URL_REGEX);
  return m ? m[0] : null;
}

function renderTextoWa(texto: string | null | undefined, fontSizePx = 16) {
  if (!texto) return null;
  const esc = texto.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const comLinks = esc.replace(URL_REGEX,
    '<a href="$1" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline break-all hover:text-blue-800">$1</a>',
  );
  const html = comLinks
    .replace(/\*(.*?)\*/g, '<b>$1</b>')
    .replace(/_(.*?)_/g, '<i>$1</i>')
    .replace(/~(.*?)~/g, '<s>$1</s>');
  return <span className="whitespace-pre-wrap break-words" style={{ fontSize: `${fontSizePx}px` }} dangerouslySetInnerHTML={{ __html: html }} />;
}

// Legenda dos marcadores de status (DA-503). O traco unico sempre significou "saiu daqui, o
// WhatsApp ainda nao confirmou que chegou" — e ninguem no balcao sabia disso: durante o buraco
// de 09-11/08 a mesma atendente reenviou o mesmo orcamento cinco vezes sem nunca ver um sinal.
// O estado estava no banco e nao virava texto. Aqui ele vira.
//
// ⚠️ O texto diz "sem confirmacao", NUNCA "nao entregue" — e a diferenca nao e estilo. Medido
// em 11/08: 14% das entregas BOAS so confirmam depois de 5min e 4% depois de 1h (celular
// desligado que volta). Afirmar "nao entregue" seria falso em ~3 de 4 casos; "sem confirmacao"
// e verdade em 100% deles. Quem afirma que o canal esta doente e a faixa do topo, que olha a
// TAXA — a bolha so relata o proprio estado.
const LIMITE_SEM_CONFIRMACAO_MS = 15 * 60 * 1000;

function idadeCurta(ms: number): string {
  const min = Math.floor(ms / 60000);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  return h < 24 ? `ha ${h}h` : `ha ${Math.floor(h / 24)}d`;
}

// O `title` mora num <span> porque o icone do lucide nao aceita a prop — e como o span so
// envolve o marcador, a dica aparece exatamente sobre ele.
function comDica(dica: string, filho: ReactElement): ReactElement {
  return <span title={dica} className="inline-flex items-center cursor-help">{filho}</span>;
}

function statusIcon(status: string, criadoEm: string): ReactElement | null {
  if (status === 'queued') return comDica('Na fila de envio', <span className="text-gray-300 text-xs">...</span>);
  if (status === 'delivered') return comDica('Entregue no aparelho do cliente', <CheckCheck size={12} className="text-gray-400" />);
  if (status === 'read') return comDica('Lida pelo cliente', <CheckCheck size={12} className="text-blue-500" />);
  if (status === 'failed') return comDica('O WhatsApp recusou o envio', <span className="text-red-500 text-xs">!</span>);
  if (status !== 'sent') return null;

  // Passou do limite sem recibo: o traco ganha COR (ambar, o mesmo da faixa do topo) alem do
  // texto. So o tooltip nao bastaria — sinal que exige passar o mouse pra existir nao alcanca
  // quem nao esta procurando por ele, e quem precisa deste nao sabe que ele existe.
  const idadeMs = Date.now() - (parseSPDate(criadoEm)?.getTime() ?? Date.now());
  if (idadeMs >= LIMITE_SEM_CONFIRMACAO_MS) {
    return comDica(
      `Sem confirmacao de entrega ha ${idadeCurta(idadeMs)} — o cliente pode nao ter recebido. Confirme por outro caminho antes de reenviar.`,
      <Check size={12} style={{ color: '#b45309' }} />,
    );
  }
  return comDica('Enviada — aguardando confirmacao de entrega', <Check size={12} className="text-gray-400" />);
}

// Registry global de players montados — permite autoplay sequencial sem Context
const audioRegistry = new Map<number, { criadoEm: string; play: () => void }>();
const VELOCIDADES = [1, 1.5, 2];

interface ReprodutorAudioProps {
  url: string;
  msgId: number;
  criadoEm: string;
  transcricao?: string | null;
  permiteTranscricao?: boolean;
  onTranscrever?: () => Promise<void>;
  onUsarComoMensagem?: (texto: string) => void;
}

function ReprodutorAudio({ url, msgId, criadoEm, transcricao, permiteTranscricao, onTranscrever, onUsarComoMensagem }: ReprodutorAudioProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [tocando, setTocando] = useState(false);
  const [progresso, setProgresso] = useState(0);
  const [velocidade, setVelocidade] = useState<number>(() => {
    const salvo = localStorage.getItem('fdc.audio.velocidade');
    return salvo ? Number(salvo) : 1;
  });
  const [duracao, setDuracao] = useState(0);
  const [posicao, setPosicao] = useState(0);
  const [arrastando, setArrastando] = useState(false);
  const [transcrevendo, setTranscrevendo] = useState(false);
  const [aberto, setAberto] = useState(true);

  async function transcrever() {
    if (!onTranscrever || transcrevendo) return;
    setTranscrevendo(true);
    try { await onTranscrever(); setAberto(true); } finally { setTranscrevendo(false); }
  }

  // Aplica velocidade ao elemento de audio sempre que mudar (cobre init + sync entre players)
  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = velocidade;
  }, [velocidade]);

  // Pausa este player quando outro começa
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail !== msgId && audioRef.current && !audioRef.current.paused) {
        audioRef.current.pause();
        setTocando(false);
      }
    };
    window.addEventListener('audio-play', handler);
    return () => window.removeEventListener('audio-play', handler);
  }, [msgId]);

  // Sincroniza velocidade quando outro player muda
  useEffect(() => {
    const handler = (e: Event) => {
      setVelocidade((e as CustomEvent<number>).detail);
    };
    window.addEventListener('audio-velocidade', handler);
    return () => window.removeEventListener('audio-velocidade', handler);
  }, []);

  // Registra este player no registry global para autoplay sequencial
  useEffect(() => {
    const play = () => {
      const a = audioRef.current;
      if (!a) return;
      window.dispatchEvent(new CustomEvent('audio-play', { detail: msgId }));
      a.play().catch(() => {});
      setTocando(true);
    };
    audioRegistry.set(msgId, { criadoEm, play });
    return () => { audioRegistry.delete(msgId); };
  }, [msgId, criadoEm]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (tocando) a.pause();
    else {
      window.dispatchEvent(new CustomEvent('audio-play', { detail: msgId }));
      a.play();
    }
    setTocando(!tocando);
  };

  const cicloVelocidade = () => {
    const idx = (VELOCIDADES.indexOf(velocidade) + 1) % VELOCIDADES.length;
    const nova = VELOCIDADES[idx]!;
    setVelocidade(nova);
    localStorage.setItem('fdc.audio.velocidade', String(nova));
    if (audioRef.current) audioRef.current.playbackRate = nova;
    window.dispatchEvent(new CustomEvent('audio-velocidade', { detail: nova }));
  };

  const fmt = (s: number) => {
    if (!s || !isFinite(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const seek = (clientX: number) => {
    if (!trackRef.current || !audioRef.current || !duracao) return;
    const rect = trackRef.current.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    audioRef.current.currentTime = pct * duracao;
    setProgresso(pct * 100);
    setPosicao(pct * duracao);
  };

  return (
    <div className="min-w-[220px]">
      <div className="flex items-center gap-2">
        <button onClick={toggle} className="w-8 h-8 rounded-full bg-white/60 flex items-center justify-center hover:bg-white/80 flex-shrink-0">
          {tocando ? <Pause size={14} /> : <Play size={14} />}
        </button>
        <div className="flex-1 min-w-0">
          <div
            ref={trackRef}
            className="relative h-5 flex items-center cursor-pointer select-none"
            onPointerDown={(e) => { setArrastando(true); seek(e.clientX); e.currentTarget.setPointerCapture(e.pointerId); }}
            onPointerMove={(e) => { if (arrastando) seek(e.clientX); }}
            onPointerUp={() => setArrastando(false)}
          >
            <div className="w-full h-1.5 bg-black/10 rounded-full overflow-hidden">
              <div className="h-full bg-gray-600 rounded-full" style={{ width: `${progresso}%` }} />
            </div>
            <div className="absolute w-3.5 h-3.5 bg-gray-700 rounded-full shadow -translate-x-1/2 hover:scale-110 transition-transform" style={{ left: `${progresso}%` }} />
          </div>
          <div className="flex justify-between">
            <span className="text-[10px] text-gray-500">{fmt(posicao)}</span>
            <span className="text-[10px] text-gray-500">{fmt(duracao)}</span>
          </div>
        </div>
        <button onClick={cicloVelocidade} className="text-[10px] font-bold bg-black/10 hover:bg-black/20 rounded px-1.5 py-0.5 flex-shrink-0 min-w-[32px] text-center" title="Velocidade">
          {velocidade}x
        </button>
        {permiteTranscricao && (
          <button
            onClick={transcricao ? () => setAberto((a) => !a) : transcrever}
            disabled={transcrevendo}
            className="flex items-center justify-center w-7 h-7 rounded-full bg-black/10 hover:bg-black/20 disabled:opacity-50 flex-shrink-0"
            title={transcrevendo ? 'Transcrevendo...' : transcricao ? (aberto ? 'Esconder transcricao' : 'Mostrar transcricao') : 'Transcrever audio'}
          >
            {transcrevendo
              ? <span className="inline-block w-3 h-3 border-2 border-gray-400 border-t-gray-700 rounded-full animate-spin" />
              : <FileAudio size={14} className="text-gray-700" />}
          </button>
        )}
      </div>
      {transcricao && aberto && (
        <div className="mt-1.5 px-2 py-1.5 rounded bg-white/90 border border-black/10 text-sm text-gray-800 whitespace-pre-wrap">
          {transcricao}
          {onUsarComoMensagem && (
            <button
              onClick={() => onUsarComoMensagem(transcricao)}
              className="mt-2 flex items-center gap-1 text-xs font-bold rounded px-2 py-1 hover:brightness-95"
              style={{ backgroundColor: '#fde68a', color: '#000', border: '1px solid #b45309' }}
              title="Jogar o texto no campo de digitacao pra revisar e enviar ao cliente"
            >
              <Send size={12} /> Usar como mensagem
            </button>
          )}
        </div>
      )}
      <audio
        ref={audioRef}
        src={url}
        onLoadedMetadata={() => { if (audioRef.current) setDuracao(audioRef.current.duration); }}
        onTimeUpdate={() => {
          if (audioRef.current && !arrastando) {
            setProgresso((audioRef.current.currentTime / audioRef.current.duration) * 100);
            setPosicao(audioRef.current.currentTime);
          }
        }}
        onEnded={() => {
          setTocando(false); setProgresso(0); setPosicao(0);
          // Toca o próximo áudio na ordem cronológica. Compara via parseSPDate (mesma
          // normalização da timeline): criado_em chega em formatos diferentes (Z via socket,
          // string crua via GET) e a comparação de string crua NÃO é monotônica — um áudio
          // antigo em formato 'T' pode "ser maior" que um novo em formato com espaço, fazendo
          // o último áudio achar um "próximo" anterior e repetir tudo em loop infinito.
          // msgId (serial do banco) desempata áudios no mesmo segundo (crua tem precisão de seg).
          const atualMs = parseSPDate(criadoEm)?.getTime() ?? 0;
          const proximo = Array.from(audioRegistry.entries())
            .map(([id, v]) => ({ v, id, ms: parseSPDate(v.criadoEm)?.getTime() ?? 0 }))
            .filter((x) => x.ms > atualMs || (x.ms === atualMs && x.id > msgId))
            .sort((a, b) => a.ms - b.ms || a.id - b.id)[0];
          proximo?.v.play();
        }}
      />
    </div>
  );
}

function MensagemImagem({ url, legenda, pix }: { url: string; legenda: string | null; pix?: boolean }) {
  const [aberta, setAberta] = useState(false);
  const [falhou, setFalhou] = useState(false);
  // Pix: QR 15% menor (238/255 = 280/300 * .85) e legenda formatada (negrito + linhas) a 14px.
  const imgClasse = pix
    ? 'rounded-lg max-w-full md:max-w-[238px] max-h-[255px] object-cover'
    : 'rounded-lg max-w-full md:max-w-[280px] max-h-[300px] object-cover';
  return (
    <>
      <div className="cursor-pointer" onClick={() => setAberta(true)}>
        {falhou ? (
          <div className="flex items-center gap-2 px-4 py-3 bg-white/50 rounded-lg border border-black/10 min-w-[200px]">
            <FileText size={20} className="text-gray-400" />
            <span className="text-sm text-gray-500">Imagem nao carregou</span>
            <a href={url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="ml-auto text-blue-500 hover:text-blue-700">
              <Download size={16} />
            </a>
          </div>
        ) : (
          <img src={url} alt="Imagem" className={imgClasse} loading="lazy" onError={() => setFalhou(true)} />
        )}
        {legenda && (pix
          ? <div className="mt-1" onClick={(e) => e.stopPropagation()}>{renderTextoWa(legenda, 14)}</div>
          // `whitespace-pre-line` como na legenda de documento logo abaixo: sem ele a legenda de
          // imagem com varias linhas (o fly do orcamento, 07/10/2026) aparecia emendada na bolha,
          // enquanto o cliente a recebia linha a linha.
          : <p className="mt-1 text-sm whitespace-pre-line">{legenda}</p>)}
      </div>
      {aberta && !falhou && <LightboxImagem imagens={[{ url, legenda }]} onFechar={() => setAberta(false)} />}
    </>
  );
}

function MensagemDocumento({ url, nome, mime, legenda }: { url: string; nome: string | null; mime: string | null; legenda: string | null }) {
  const [aberto, setAberto] = useState(false);
  const ePdf = mime === 'application/pdf' || (nome && nome.toLowerCase().endsWith('.pdf'));
  if (ePdf) {
    const pdfUrl = url + (url.includes('#') ? '&' : '#') + 'navpanes=0&pagemode=none';
    return (
      <>
        <button type="button" onClick={() => setAberto(true)} className="block group w-full text-left">
          <ThumbnailPdf url={url} />
          <div className="flex items-center gap-2 px-2 py-1.5 mt-1 bg-white/50 rounded-lg group-hover:bg-white/70 border border-black/5">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-800 truncate">{nome || 'Documento'}</p>
              <p className="text-[10px] text-gray-400 uppercase">PDF</p>
            </div>
            <Download
              size={16}
              className="text-gray-400 flex-shrink-0 hover:text-gray-700"
              onClick={(e) => {
                e.stopPropagation();
                const a = document.createElement('a');
                a.href = url;
                a.download = nome || 'documento.pdf';
                a.click();
              }}
            />
          </div>
        </button>
        {legenda && <p className="mt-1 text-sm whitespace-pre-line">{legenda}</p>}
        {aberto && (
          <div
            className="fixed inset-0 z-[100] bg-black/85 flex flex-col"
            onClick={() => setAberto(false)}
          >
            <div className="flex items-center justify-between px-4 py-2 bg-black/40 text-white" onClick={(e) => e.stopPropagation()}>
              <span className="text-sm font-medium truncate">{nome || 'Documento'}</span>
              <div className="flex items-center gap-2">
                <a
                  href={url}
                  download={nome || 'documento.pdf'}
                  className="p-2 rounded hover:bg-white/10"
                  title="Baixar"
                >
                  <Download size={18} />
                </a>
                <button
                  type="button"
                  onClick={() => setAberto(false)}
                  className="p-2 rounded hover:bg-white/10"
                  title="Fechar (Esc)"
                >
                  ×
                </button>
              </div>
            </div>
            <iframe
              src={pdfUrl}
              title={nome || 'PDF'}
              className="flex-1 bg-white"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </>
    );
  }
  return (
    <>
      <a href={url} download={nome || 'Documento'} className="flex items-center gap-3 px-3 py-2.5 bg-white/50 rounded-lg hover:bg-white/70 border border-black/5 min-w-[200px]">
        <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
          <Download size={16} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-800 truncate">{nome || 'Documento'}</p>
          <p className="text-[10px] text-gray-400 uppercase">{mime || 'Arquivo'}</p>
        </div>
        <Download size={16} className="text-gray-400 flex-shrink-0" />
      </a>
      {legenda && <p className="mt-1 text-sm whitespace-pre-line">{legenda}</p>}
    </>
  );
}

function MensagemCitada({ msg, contatoNome }: { msg: Mensagem | null; contatoNome: string }) {
  if (!msg) return null;
  const autor = msg.direcao === 'inbound' ? contatoNome : (msg.assinatura || 'Voce');
  const onClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!msg.id) return;
    const el = document.getElementById(`msg-${msg.id}`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('ring-2', 'ring-amber-400');
    setTimeout(() => el.classList.remove('ring-2', 'ring-amber-400'), 2000);
  };
  return (
    <div onClick={onClick} className="border-l-2 border-blue-400 pl-2 mb-1 py-0.5 text-xs text-gray-600 bg-black/5 rounded-r cursor-pointer hover:bg-black/10">
      <span className="font-medium">{autor}</span>
      <p className="truncate max-w-[250px]">{msg.texto || (msg.tipo === 'image' ? '[Imagem]' : '[Midia]')}</p>
    </div>
  );
}

interface Reaction {
  emoji: string;
  jid: string;
  name: string;
  fromMe: boolean;
  at: string;
}

interface Props {
  msg: Mensagem;
  msgCitada: Mensagem | null;
  contato: Contato | null;
  inbox: Inbox;
  onResponder?: (m: Mensagem) => void;
  onEncaminhar?: (m: Mensagem) => void;
  onEditar?: (m: Mensagem) => void;
  onApagar?: (m: Mensagem) => void;
  onApagarParaTodos?: (m: Mensagem) => void;
  onFixar?: (m: Mensagem) => void;
  onReagir?: (m: Mensagem, emoji: string | null) => void;
  onTranscrever?: (m: Mensagem) => Promise<void>;
  onUsarComoMensagem?: (texto: string) => void;
  onConversarComNumero?: (telefone: string, nome: string) => void;
  onIniciarSelecao?: (m: Mensagem) => void;
  modoSelecao?: boolean;
  selecionada?: boolean;
  onToggleSelecao?: (m: Mensagem) => void;
}

// ----- Foto do contato no cartao (vCard) -----
// O cartao so carrega NOME e TELEFONE — a foto tem que ser resolvida pelo numero contra
// os contatos ja conhecidos. Chave = ultimos 8 digitos, que casam a mesma linha com e sem
// o 9o digito (o vCard traz +55 64 99218-7041; o contato no banco e 556492187041).
// Cache module-level + dedup em-voo: o mesmo cartao reaparece a cada render da thread, e
// a foto de um contato nao muda no meio da conversa. Molde: o cache do LinkPreview.
const fotoCartaoCache = new Map<string, string | null>();
const fotoCartaoEmVoo = new Map<string, Promise<string | null>>();

function chaveNumero(digits: string): string {
  return digits.length >= 8 ? digits.slice(-8) : digits;
}

function buscarFotoDoNumero(digits: string): Promise<string | null> {
  const k = chaveNumero(digits);
  const jaTem = fotoCartaoCache.get(k);
  if (jaTem !== undefined) return Promise.resolve(jaTem);
  let p = fotoCartaoEmVoo.get(k);
  if (!p) {
    // A busca do backend e ILIKE sobre telefone e jid — os 8 digitos finais casam as duas grafias.
    p = atendimentoApi.listarContatos({ busca: k, sem_grupo: true, limit: 5 })
      .then((cs) => cs.find((c) => c.foto_url)?.foto_url ?? null)
      .catch(() => null)
      .then((url) => { fotoCartaoCache.set(k, url); fotoCartaoEmVoo.delete(k); return url; });
    fotoCartaoEmVoo.set(k, p);
  }
  return p;
}

function AvatarCartaoContato({ nome, telefone }: { nome: string; telefone: string }) {
  const digitos = telefone.replace(/\D/g, '');
  const k = chaveNumero(digitos);
  const [foto, setFoto] = useState<string | null>(() => fotoCartaoCache.get(k) ?? null);
  useEffect(() => {
    if (digitos.length < 8) return;
    let vivo = true;
    void buscarFotoDoNumero(digitos).then((url) => { if (vivo) setFoto(url); });
    return () => { vivo = false; };
  }, [k, digitos]);
  return <Avatar nome={nome} fotoUrl={foto} tamanho={40} />;
}

// Telefone do cartao de contato: o inbound chega ja formatado pelo WhatsApp, o nosso
// envio antigo gravou so digitos (+556492913025). Formata quando vem cru; senao respeita.
function fmtTelCartao(raw: string): string {
  const d = raw.replace(/\D/g, '');
  if (/[\s()-]/.test(raw)) return raw;
  const m = /^55(\d{2})(\d{4,5})(\d{4})$/.exec(d);
  if (m) return `+55 ${m[1]} ${m[2]}-${m[3]}`;
  const n = /^(\d{2})(\d{4,5})(\d{4})$/.exec(d);
  return n ? `(${n[1]}) ${n[2]}-${n[3]}` : raw;
}

// Chave como aparece no cartao (CNPJ/CPF pontuados) e como vai pro copiar: CNPJ/CPF so
// com os numeros, como a chave que o nosso proprio botao Pix manda (pedido dele, 29/09).
function exibirChavePix(c: ChavePixFluxo): { rotulo: string; valor: string; copiar: string } {
  const t = c.tipo.toUpperCase();
  if (t.includes('CNPJ') || t.includes('CPF')) {
    return { rotulo: t.includes('CNPJ') ? 'CNPJ' : 'CPF', valor: formatCpfCnpj(c.chave), copiar: c.chave.replace(/\D/g, '') };
  }
  const rotulo = t.includes('PHONE') ? 'Telefone' : t.includes('EMAIL') ? 'E-mail' : 'Chave';
  return { rotulo, valor: c.chave, copiar: c.chave };
}

function CartaoPixRecebido({ chaves }: { chaves: ChavePixFluxo[] }) {
  const [copiada, setCopiada] = useState<string | null>(null);
  const [falhou, setFalhou] = useState(false);
  async function copiar(valor: string) {
    try {
      await navigator.clipboard.writeText(valor);
      setFalhou(false);
      setCopiada(valor);
      setTimeout(() => setCopiada((v) => (v === valor ? null : v)), 2500);
    } catch {
      setFalhou(true);
    }
  }
  return (
    <div className="min-w-[240px]">
      {chaves.map((c, i) => {
        const e = exibirChavePix(c);
        return (
          <div key={c.chave} className={i > 0 ? 'border-t border-black/10 mt-1' : ''}>
            <div className="flex items-center gap-3 px-3 py-2.5">
              <span className="w-10 h-10 rounded-full flex items-center justify-center text-xl flex-shrink-0" style={{ backgroundColor: 'rgba(0,0,0,0.08)' }}>💠</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800">{c.nome || 'Pix'}</p>
                <p className="text-xs text-gray-500" style={{ overflowWrap: 'anywhere' }}>{e.rotulo}: <span className="select-all">{e.valor}</span></p>
              </div>
            </div>
            <button
              type="button"
              onClick={(ev) => { ev.stopPropagation(); void copiar(e.copiar); }}
              className="w-full inline-flex items-center justify-center gap-2 text-[15px] font-semibold py-2.5 border-t hover:bg-black/5 transition"
              style={{ color: 'var(--primary)', borderColor: 'rgba(0,0,0,0.10)', whiteSpace: 'nowrap' }}
            >
              {copiada === e.copiar ? <Check size={16} /> : <Copy size={16} />}
              {copiada === e.copiar ? 'Chave copiada' : 'Copiar chave Pix'}
            </button>
          </div>
        );
      })}
      {falhou && <p className="text-xs text-gray-600 px-3 pb-2">Não consegui copiar. Selecione a chave acima e copie com Ctrl+C.</p>}
    </div>
  );
}

function fmtHora(iso: string): string {
  // Backend grava criado_em como TIMESTAMP sem TZ com wall-clock SP — usa helper compartilhado pra corrigir.
  return formatHoraSP(iso);
}

export function MensagemBubble({ msg, msgCitada, contato, inbox, onResponder, onEncaminhar, onEditar, onApagar, onApagarParaTodos, onFixar, onReagir, onTranscrever, onUsarComoMensagem, onConversarComNumero, onIniciarSelecao, modoSelecao = false, selecionada = false, onToggleSelecao }: Props) {
  const [menuAberto, setMenuAberto] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left?: number; right?: number } | null>(null);
  const [pickerPos, setPickerPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const sair = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reactions: Reaction[] = (msg.metadata?.reactions as Reaction[] | undefined) ?? [];
  const reactionPropria = reactions.find((r) => r.fromMe) ?? null;

  function abrirPicker() {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const W = 320, H = 400;
    const flipUp = rect.bottom + H > window.innerHeight;
    const lado = ehSaida ? rect.right - W : rect.left;
    setPickerPos({
      top: flipUp ? Math.max(8, rect.top - H - 4) : rect.bottom + 4,
      left: Math.max(8, Math.min(window.innerWidth - W - 8, lado)),
    });
  }
  function escolherEmoji(data: EmojiClickData) {
    setPickerPos(null);
    onReagir?.(msg, data.emoji);
  }
  function removerReacaoPropria() {
    onReagir?.(msg, null);
  }
  const ehSaida = msg.direcao === 'outbound';
  const hora = fmtHora(msg.criado_em);
  const recente = (Date.now() - (parseSPDate(msg.criado_em)?.getTime() ?? 0)) < 600000;

  const url = primeiraUrl(msg.texto);
  const multilinha = (msg.texto?.includes('\n') ?? false) || ((msg.texto?.length ?? 0) > 80);

  const onEntrar = () => { if (sair.current) { clearTimeout(sair.current); sair.current = null; } };
  const onSair = () => { sair.current = setTimeout(() => setMenuAberto(false), 300); };

  const conteudo = (() => {
    switch (msg.tipo) {
      case 'image':
        return <MensagemImagem url={msg.midia_url ?? ''} legenda={msg.texto} pix={Boolean((msg.metadata as Record<string, unknown> | undefined)?.pix)} />;
      // O backend grava 'gif' pra video com gifPlayback; sem este case o anexo caia no
      // default e so a legenda aparecia, com a lista anunciando "GIF" ao lado.
      case 'video':
      case 'gif':
        return (
          <>
            <video
              src={msg.midia_url ?? ''}
              controls={msg.tipo === 'video'}
              autoPlay={msg.tipo === 'gif'}
              loop={msg.tipo === 'gif'}
              muted={msg.tipo === 'gif'}
              playsInline
              preload="metadata"
              className="rounded-lg max-w-full md:max-w-[320px] max-h-[300px]"
            />
            {msg.texto && <p className="mt-1 text-sm">{msg.texto}</p>}
          </>
        );
      case 'sticker':
        return <img src={msg.midia_url ?? ''} alt="Sticker" className="max-w-[160px] max-h-[160px]" />;
      case 'audio':
        return (
          <ReprodutorAudio
            url={msg.midia_url ?? ''}
            msgId={msg.id}
            criadoEm={msg.criado_em}
            transcricao={(msg.metadata?.transcricao as { texto?: string } | undefined)?.texto ?? null}
            permiteTranscricao={!!onTranscrever}
            onTranscrever={onTranscrever ? () => onTranscrever(msg) : undefined}
            onUsarComoMensagem={ehSaida ? onUsarComoMensagem : undefined}
          />
        );
      case 'document':
        return <MensagemDocumento url={msg.midia_url ?? ''} nome={msg.midia_nome} mime={msg.midia_mime} legenda={msg.texto} />;
      case 'contact': {
        const blocos = (msg.texto ?? '').split('---').map((b) => {
          const linhas = b.trim().split('\n');
          return { nome: linhas[0] || 'Contato', telefone: linhas[1] || '' };
        });
        return (
          <div className="min-w-[220px]">
            {blocos.map((c, i) => {
              const digitos = c.telefone.replace(/\D/g, '');
              return (
              <div key={i} className={i > 0 ? 'border-t border-black/10 mt-1' : ''}>
                <div className="flex items-center gap-3 px-3 py-2.5">
                  <AvatarCartaoContato nome={c.nome} telefone={c.telefone} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800">{c.nome}</p>
                    {c.telefone && <p className="text-xs text-gray-500">{fmtTelCartao(c.telefone)}</p>}
                  </div>
                </div>
                {/* Mesma acao do cartao no celular: abre a conversa com o numero do cartao.
                    Sem isso o vCard so era legivel aqui — pra falar com ele, o operador
                    tinha que copiar o numero e abrir "Nova conversa" na mao. */}
                {digitos.length >= 10 && onConversarComNumero && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onConversarComNumero(digitos, c.nome); }}
                    className="w-full text-center text-[15px] font-semibold py-2.5 border-t hover:bg-black/5 transition"
                    style={{ color: 'var(--primary)', borderColor: 'rgba(0,0,0,0.10)' }}
                  >
                    Conversar
                  </button>
                )}
              </div>
            );})}
          </div>
        );
      }
      case 'location': {
        const meta = msg.metadata ?? {};
        const lat = Number(meta.latitude);
        const lng = Number(meta.longitude);
        const nome = meta.name || '';
        const end = meta.address || '';
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return <p className="text-sm text-gray-500 italic">Localizacao sem coordenadas</p>;
        const d = 0.005;
        const bbox = `${lng - d},${lat - d},${lng + d},${lat + d}`;
        const osm = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat},${lng}`;
        const gmap = `https://www.google.com/maps?q=${lat},${lng}`;
        return (
          <a href={gmap} target="_blank" rel="noopener noreferrer" className="block w-[260px] rounded-lg overflow-hidden bg-white shadow-sm">
            <iframe src={osm} className="w-full h-[150px] border-0 pointer-events-none" loading="lazy" title="mapa" />
            <div className="px-3 py-2">
              {nome && <p className="text-sm font-medium text-gray-800 truncate">{nome}</p>}
              {end && <p className="text-xs text-gray-500 line-clamp-2">{end}</p>}
              {!nome && !end && <p className="text-xs text-gray-500">{lat.toFixed(5)}, {lng.toFixed(5)}</p>}
              <p className="text-xs text-blue-600 mt-1">Abrir no Google Maps</p>
            </div>
          </a>
        );
      }
      case 'call':
        return (
          <div className="flex items-center gap-2 text-red-500 py-1">
            <PhoneMissed size={18} />
            <span className="text-sm font-medium">{msg.texto || 'Ligacao perdida'}</span>
          </div>
        );
      default: {
        // Cobranca Pix do WhatsApp Business: o cartao com "Copiar chave Pix" sai do
        // envelope gravado pelo backend, pela mesma peca que escreveu o texto.
        const fluxo = (msg.metadata as Record<string, unknown> | undefined)?.fluxo_nativo;
        const chavesPix = fluxo ? pixDoFluxoNativo(fluxo).chaves : [];
        if (chavesPix.length) return <CartaoPixRecebido chaves={chavesPix} />;
        return (
          <>
            {url && <LinkPreviewCard url={url} />}
            {renderTextoWa(msg.texto)}
          </>
        );
      }
    }
  })();

  // Em modo selecao, click na linha inteira toggle e bloqueia interacoes internas (lightbox/menu/audio).
  const onLinhaClick = modoSelecao ? (e: React.MouseEvent) => { e.stopPropagation(); e.preventDefault(); onToggleSelecao?.(msg); } : undefined;

  return (
    <div
      id={`msg-${msg.id}`}
      className={`group flex mb-1.5 ${ehSaida ? 'justify-end' : 'justify-start'} relative ${modoSelecao ? 'cursor-pointer' : ''} ${selecionada ? 'bg-blue-100/40 -mx-2 px-2 rounded' : ''}`}
      onMouseEnter={onEntrar}
      onMouseLeave={onSair}
      onClickCapture={onLinhaClick}
    >
      {modoSelecao && (
        <div className={`flex items-center self-center flex-shrink-0 ${ehSaida ? 'order-last ml-2' : 'mr-2'}`}>
          <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition ${selecionada ? 'bg-blue-500 border-blue-500' : 'bg-white border-gray-400'}`}>
            {selecionada && <Check size={12} className="text-white" />}
          </span>
        </div>
      )}

      {!ehSaida && contato && (
        <div className="flex-shrink-0 self-end mb-1 mr-1.5">
          <Avatar nome={contato.nome || contato.push_name || ''} fotoUrl={contato.foto_url} tamanho={34} />
        </div>
      )}
      <div className="relative" style={{ maxWidth: 'min(85%, 520px)', width: multilinha ? '100%' : 'fit-content', paddingBottom: reactions.length ? 14 : 0 }}>
        <div className={ehSaida ? 'bubble-out' : 'bubble-in'} style={modoSelecao ? { pointerEvents: 'none' } : undefined}>
          {ehSaida && msg.assinatura && (
            <div className="flex items-center gap-1.5 mb-0.5">
              <span className="font-bold text-gray-700" style={{ fontSize: '19.2px' }}>{msg.assinatura}</span>
            </div>
          )}
          {msg.encaminhada && (
            <div className="flex items-center gap-1 mb-0.5 text-gray-400">
              <Forward size={12} />
              <span className="text-[11px] italic">Encaminhada</span>
            </div>
          )}
          <MensagemCitada msg={msgCitada} contatoNome={contato?.nome ?? ''} />
          {conteudo}
          <div className={`flex items-center gap-1 mt-0.5 ${ehSaida ? 'justify-end' : 'justify-start'}`}>
            {/* Quando o WhatsApp manda a edicao cifrada e nao conseguimos ler o texto novo,
                o selo tem que dizer isso: "Editada" sozinho sobre o texto ANTIGO faria o
                operador separar mercadoria pela lista errada achando que esta atualizada.
                Laranja de atencao (nunca vermelho/verde — ver daltonismo). */}
            {msg.editada && (msg.metadata as any)?.edicao_ilegivel ? (
              <span
                className="text-[10px] italic flex items-center gap-0.5 px-1 rounded"
                style={{ backgroundColor: '#f97316', color: '#000' }}
                title="O cliente editou esta mensagem no celular, mas o WhatsApp nao entregou o texto novo. Confira no aparelho."
              >
                <Pencil size={9} /> Editada — texto novo nao recebido
              </span>
            ) : msg.editada ? (
              /* Lilas: e a unica cor que nesta tela nao significa outra coisa — branco e
                 amarelo (#fff176) sao as proprias bolhas, azul e o tema, laranja acabou de
                 virar "nao recebi o texto novo". Em cinza 10px o selo sumia, e o operador
                 relia a mensagem sem saber que o cliente a tinha trocado. */
              <span
                className="text-[10px] italic flex items-center gap-0.5 px-1 rounded"
                style={{ backgroundColor: '#c4b5fd', color: '#000' }}
                title="O cliente editou esta mensagem — o texto acima ja e o novo."
              >
                <Pencil size={9} /> Editada
              </span>
            ) : null}
            <span className="text-[10px] text-gray-400">{hora}</span>
            {ehSaida && statusIcon(msg.status, msg.criado_em)}
          </div>
        </div>
        {reactions.length > 0 && (
          <div
            className={`absolute -bottom-1 ${ehSaida ? 'left-2' : 'right-2'} flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-white shadow border border-gray-200 text-sm`}
            title={reactions.map((r) => `${r.emoji} ${r.name}`).join(' · ')}
            onClick={(e) => {
              if (modoSelecao) return;
              if (reactionPropria) { e.stopPropagation(); removerReacaoPropria(); }
            }}
            style={{ cursor: reactionPropria && !modoSelecao ? 'pointer' : 'default' }}
          >
            {reactions.map((r, i) => (
              <span key={i}>{r.emoji}</span>
            ))}
          </div>
        )}
      </div>

      {!modoSelecao && (
        <div className={`flex items-start gap-0.5 self-center opacity-0 group-hover:opacity-100 transition-opacity ${ehSaida ? 'order-first mr-1.5' : 'ml-1.5'}`}>
          <button
            ref={btnRef}
            onClick={() => {
              if (!menuAberto && btnRef.current) {
                const rect = btnRef.current.getBoundingClientRect();
                const menuH = 220;
                const flipUp = rect.bottom + menuH > window.innerHeight;
                setMenuPos({
                  top: flipUp ? Math.max(4, rect.top - menuH) : rect.bottom + 4,
                  left: ehSaida ? undefined : rect.left,
                  right: ehSaida ? (window.innerWidth - rect.right) : undefined,
                });
              }
              setMenuAberto(!menuAberto);
            }}
            className="p-1.5 rounded-full shadow-sm border border-yellow-600 hover:brightness-95 transition"
            style={{ backgroundColor: '#fde68a' }}
            title="Opcoes da mensagem"
          >
            <MoreVertical size={18} style={{ color: '#000' }} strokeWidth={2.5} />
          </button>
        </div>
      )}

      {menuAberto && menuPos && !modoSelecao && (
        <div
          className="fixed z-[9999] bg-white rounded-lg shadow-lg border py-1 min-w-[160px]"
          style={{ top: menuPos.top, left: menuPos.left, right: menuPos.right }}
          onMouseEnter={onEntrar} onMouseLeave={onSair}
        >
          <ItemMenu icon={<Reply size={14} />} label="Responder" onClick={() => { setMenuAberto(false); onResponder?.(msg); }} />
          <ItemMenu icon={<Smile size={14} />} label={reactionPropria ? 'Trocar reacao' : 'Reagir'} onClick={() => { setMenuAberto(false); abrirPicker(); }} />
          <ItemMenu icon={<Forward size={14} />} label="Encaminhar" onClick={() => { setMenuAberto(false); onEncaminhar?.(msg); }} />
          <ItemMenu icon={<Pin size={14} />} label="Fixar" onClick={() => { setMenuAberto(false); onFixar?.(msg); }} />
          <ItemMenu icon={<CheckSquare size={14} />} label="Selecionar" onClick={() => { setMenuAberto(false); onIniciarSelecao?.(msg); }} />
          {ehSaida && recente && msg.tipo === 'text' && inbox !== 'atendimento' && (
            <ItemMenu icon={<Pencil size={14} />} label="Editar" onClick={() => { setMenuAberto(false); onEditar?.(msg); }} />
          )}
          {/* ⚠️ "da loja", nao "Apagar" seco: ao lado de "Apagar para todos" o rotulo
              curto nao dizia de ONDE some, e os dois viravam duas versoes da mesma
              palavra. Na caixa `app` isto agora ESCONDE do lado da loja em vez de
              destruir a linha ([DA-574]) — o cliente continua com a mensagem dele. */}
          <ItemMenu icon={<Trash2 size={14} />} label="Apagar da loja" className="text-red-500" onClick={() => { setMenuAberto(false); onApagar?.(msg); }} />
          {ehSaida && inbox !== 'atendimento' && (
            <ItemMenu icon={<Trash2 size={14} />} label="Apagar para todos" className="text-red-500" onClick={() => { setMenuAberto(false); onApagarParaTodos?.(msg); }} />
          )}
        </div>
      )}

      {pickerPos && createPortal(
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => setPickerPos(null)} />
          <div className="fixed z-[9999]" style={{ top: pickerPos.top, left: pickerPos.left }} onClick={(e) => e.stopPropagation()}>
            <EmojiPicker
              onEmojiClick={escolherEmoji}
              searchPlaceholder="Buscar emoji..."
              width={320}
              height={400}
              previewConfig={{ showPreview: false }}
              skinTonesDisabled
              lazyLoadEmojis
            />
          </div>
        </>,
        document.body,
      )}
    </div>
  );
}

function ItemMenu({ icon, label, onClick, className = '' }: { icon: ReactElement; label: string; onClick: () => void; className?: string }) {
  return (
    <button onClick={onClick} className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-100 transition ${className}`}>
      {icon} {label}
    </button>
  );
}
